import { useMemo } from 'react';

import { JobQuoteVariant } from 'components/JobQuote';
import { useJobQuoteContext } from 'components/JobQuote/context';

export type JobQuotePriceType = 'simple' | 'deposit';

export interface JobQuotePricesHook {
  type: JobQuotePriceType;
  subTotal: number;
  vatTotal: number;
  jobTotal: number;
  depositAmount: number;
  totalToPay: number;
  gridClassName: string;
  variant: JobQuoteVariant;
  isLoading: boolean;
}

export default function useJobQuotePrices(): JobQuotePricesHook {
  const { job, isLoading, variant } = useJobQuoteContext();

  const type: JobQuotePriceType = useMemo(() => {
    if (!job || isLoading) return 'simple';
    const { deposit_value: depositValue, deposit_type: depositType } = job;
    if (depositType === 'no_deposit') return 'simple';
    return depositValue.amount === 0 ? 'simple' : 'deposit';
  }, [job, isLoading]);

  const subTotal = useMemo(
    () => job?.total?.amount_vat_exclusive?.amount ?? 0,
    [job],
  );

  const vatTotal = useMemo(() => job?.total?.amount_vat?.amount ?? 0, [job]);

  const jobTotal = useMemo(
    () => job?.total?.amount_vat_inclusive?.amount ?? 0,
    [job],
  );

  const depositAmount = useMemo(() => job?.deposit_value?.amount ?? 0, [job]);

  const totalToPay = useMemo(
    () => jobTotal - depositAmount,
    [jobTotal, depositAmount],
  );

  const gridClassName = useMemo(
    () =>
      [
        'grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 px-4 py-3',
        variant !== 'full' ? '' : 'md:gap-y-3 md:px-6 md:py-5',
      ].join(' '),
    [variant],
  );

  return {
    type,
    subTotal,
    vatTotal,
    jobTotal,
    depositAmount,
    totalToPay,
    gridClassName,
    variant,
    isLoading,
  };
}
